import React, { useState, useEffect } from 'react';
import { RefreshCw, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { DateInputBR } from '@components/DateInputBR';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { cieloWebhookService } from '@services/cieloWebhookService';
import {
  CardChargebackEntry,
  CieloNotificationLogEntry,
  CIELO_CHANGE_TYPES,
  CIELO_OUTCOMES,
} from '@/types/cieloWebhook';

type View = 'notifications' | 'chargebacks';

const PAGE_SIZE = 20;

/** Mesmas classes do `Input` do design system, para o campo de data não destoar. */
const dateInputCls =
  'input-field w-full px-3 py-2 text-sm';

/** Últimos 30 dias — a janela que interessa quando se vai investigar algo. */
const defaultDateFrom = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
};

const formatDateTime = (value: string | null): string => {
  if (!value) return '—';
  // As datas vêm no fuso de Brasília, sem conversão (padrão do ERP).
  const [date, rest] = value.split('T');
  if (!date) return value;
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y} ${(rest ?? '').slice(0, 8)}`.trim();
};

const formatCurrency = (value: number): string =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Vermelho para o que exige ação, cinza para o resto. */
const outcomeClass = (outcome: number): string => {
  switch (outcome) {
    case 1:
      return 'bg-emerald-100 text-emerald-700';
    case 3:
      return 'bg-red-100 text-red-700';
    case 4:
      return 'bg-amber-100 text-amber-700';
    default:
      return 'bg-slate-100 text-slate-600';
  }
};

const isCriticalEvent = (changeType: number | null): boolean =>
  changeType === 7 || changeType === 25 || changeType === 5 || changeType === 8;

export const CieloWebhookTab: React.FC = () => {
  const [view, setView] = useState<View>('notifications');
  const [notifications, setNotifications] = useState<CieloNotificationLogEntry[]>([]);
  const [chargebacks, setChargebacks] = useState<CardChargebackEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);

  const [dateFrom, setDateFrom] = useState(defaultDateFrom());
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [changeType, setChangeType] = useState<string>('');
  const [outcome, setOutcome] = useState<string>('');
  const [paymentId, setPaymentId] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const searchModel = {
        page: currentPage,
        pageSize: PAGE_SIZE,
        dateFrom,
        dateTo,
        changeType: changeType === '' ? undefined : Number(changeType),
        outcome: outcome === '' ? undefined : Number(outcome),
        paymentId: paymentId.trim() || undefined,
      };
      if (view === 'notifications') {
        const result = await cieloWebhookService.getNotifications(searchModel);
        setNotifications(result.data);
        setTotalPages(result.pagination.totalPages);
        setTotalItems(result.pagination.total);
      } else {
        const result = await cieloWebhookService.getChargebacks(searchModel);
        setChargebacks(result.data);
        setTotalPages(result.pagination.totalPages);
        setTotalItems(result.pagination.total);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, view]);

  const applyFilters = () => {
    setExpanded(null);
    if (currentPage === 1) {
      load();
    } else {
      setCurrentPage(1);
    }
  };

  const switchView = (next: View) => {
    if (next === view) return;
    setExpanded(null);
    setCurrentPage(1);
    setView(next);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          Tudo que a Cielo envia pelo Post de Notificação é registrado aqui, inclusive os eventos
          sem tratamento. É o único rastro do que o banco informou — e a única prova disponível
          quando chega um chargeback.
        </p>
        <Button variant="outline" size="sm" onClick={load} icon={<RefreshCw size={14} />}>
          Atualizar
        </Button>
      </div>

      <div className="inline-flex rounded-lg border border-slate-200 p-1">
        <button
          onClick={() => switchView('notifications')}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            view === 'notifications' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          Notificações recebidas
        </button>
        <button
          onClick={() => switchView('chargebacks')}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            view === 'chargebacks' ? 'bg-red-50 text-red-700' : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          Chargebacks e estornos
        </button>
      </div>

      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent><p className="text-red-600 text-sm">{error}</p></CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">De</label>
              <DateInputBR className={dateInputCls} value={dateFrom} onChange={setDateFrom} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Até</label>
              <DateInputBR className={dateInputCls} value={dateTo} onChange={setDateTo} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Evento</label>
              <select
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={changeType}
                onChange={(e) => setChangeType(e.target.value)}
              >
                <option value="">Todos</option>
                {CIELO_CHANGE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            {view === 'notifications' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Resultado</label>
                  <select
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {CIELO_OUTCOMES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">PaymentId</label>
                  <Input value={paymentId} onChange={(e) => setPaymentId(e.target.value)} placeholder="Trecho do id" />
                </div>
              </>
            )}
          </div>
          <div className="flex justify-end mt-3">
            <Button size="sm" onClick={applyFilters} loading={loading}>Filtrar</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {view === 'notifications' ? (
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left w-8"></th>
                  <th className="text-left">Recebida em</th>
                  <th className="text-left">Evento</th>
                  <th className="text-left">PaymentId</th>
                  <th className="text-left">Resultado</th>
                  <th className="text-left">Observação</th>
                </tr>
              </TableHeader>
              <tbody>
                {loading ? (
                  [...Array(3)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={6}><div className="h-4 bg-slate-200 rounded animate-pulse" /></TableCell>
                    </TableRow>
                  ))
                ) : notifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                      Nenhuma notificação no período. Se o webhook acabou de ser cadastrado na Cielo,
                      a primeira só chega quando houver uma mudança em alguma transação.
                    </TableCell>
                  </TableRow>
                ) : (
                  notifications.map((item) => (
                    <React.Fragment key={item.id}>
                      <TableRow>
                        <TableCell>
                          <button
                            onClick={() => setExpanded(expanded === item.id ? null : item.id)}
                            className="text-slate-400 hover:text-slate-600"
                            aria-label="Ver corpo da notificação"
                          >
                            {expanded === item.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm">{formatDateTime(item.received_on)}</TableCell>
                        <TableCell>
                          <span className={isCriticalEvent(item.change_type) ? 'font-semibold text-red-700' : ''}>
                            {isCriticalEvent(item.change_type) && (
                              <AlertTriangle size={14} className="inline mr-1 -mt-0.5" />
                            )}
                            {item.change_type_label || '—'}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{item.payment_id ?? '—'}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${outcomeClass(item.outcome)}`}>
                            {item.outcome_label}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{item.outcome_message ?? '—'}</TableCell>
                      </TableRow>
                      {expanded === item.id && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-slate-50">
                            <div className="space-y-2 py-2">
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                <div>
                                  <span className="text-slate-500">Empresa</span>
                                  <p className="font-medium">{item.company_id ?? '—'}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500">Status na Cielo</span>
                                  <p className="font-medium">{item.queried_status ?? '—'}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500">Código de retorno</span>
                                  <p className="font-medium">{item.queried_return_code ?? '—'}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500">Origem</span>
                                  <p className="font-mono text-xs">{item.remote_addr ?? '—'}</p>
                                </div>
                              </div>
                              <div>
                                <span className="text-slate-500 text-sm">Corpo recebido</span>
                                <pre className="mt-1 p-3 bg-white border border-slate-200 rounded-lg text-xs overflow-x-auto">
                                  {item.raw_body ?? '(vazio)'}
                                </pre>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </Table>
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">Informado em</th>
                  <th className="text-left">Evento</th>
                  <th className="text-left">Referência</th>
                  <th className="text-left">Cliente</th>
                  <th className="text-left">Bandeira</th>
                  <th className="text-right">Valor</th>
                  <th className="text-left">PaymentId</th>
                </tr>
              </TableHeader>
              <tbody>
                {loading ? (
                  [...Array(3)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}><div className="h-4 bg-slate-200 rounded animate-pulse" /></TableCell>
                    </TableRow>
                  ))
                ) : chargebacks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                      Nenhum chargeback ou estorno no período.
                    </TableCell>
                  </TableRow>
                ) : (
                  chargebacks.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="whitespace-nowrap text-sm">{formatDateTime(item.notified_on)}</TableCell>
                      <TableCell>
                        <span className={item.change_type === 7 ? 'font-semibold text-red-700' : ''}>
                          {item.change_type_label}
                        </span>
                      </TableCell>
                      <TableCell>
                        {item.order_id
                          ? `Pedido site ${item.order_id}`
                          : item.comercial_proposal_id
                            ? `Proposta ${item.comercial_proposal_id}`
                            : '—'}
                      </TableCell>
                      <TableCell>{item.customer_name ?? '—'}</TableCell>
                      <TableCell>{item.brand ?? '—'}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(item.amount)}</TableCell>
                      <TableCell className="font-mono text-xs">{item.payment_id}</TableCell>
                    </TableRow>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">Total: {totalItems} registro(s)</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
              Anterior
            </Button>
            <span className="text-sm text-slate-600">Página {currentPage} de {totalPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
